import { MS_PER_MINUTE } from '../model/constants';
import { parseDate } from './date';

/** IANA time-zone name of the running environment. */
export function currentTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatterCache = new Map<string, Intl.DateTimeFormat | null>();

function formatterFor(tz: string): Intl.DateTimeFormat | null {
  if (formatterCache.has(tz)) return formatterCache.get(tz) ?? null;
  let formatter: Intl.DateTimeFormat | null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    formatter = null;
  }
  formatterCache.set(tz, formatter);
  return formatter;
}

/** Offset (ms) of `tz` at the instant `epochMs`: wall clock as UTC minus the instant. */
function offsetAt(formatter: Intl.DateTimeFormat, epochMs: number): number {
  const parts: Record<string, number> = {};
  for (const part of formatter.formatToParts(new Date(epochMs))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  const wallAsUtc = Date.UTC(
    parts['year'] ?? 1970,
    (parts['month'] ?? 1) - 1,
    parts['day'] ?? 1,
    parts['hour'] ?? 0,
    parts['minute'] ?? 0,
    parts['second'] ?? 0,
  );
  return wallAsUtc - Math.floor(epochMs / 1000) * 1000;
}

/**
 * The instant at which the wall clock of time zone `tz` shows `wallMin` minutes after 00:00 of
 * `personalDate`. Returns null when `tz` is not a valid IANA name. For a wall time that does
 * not exist (daylight-saving gap) the result is the instant after the gap.
 */
export function zonedWallToEpoch(personalDate: string, wallMin: number, tz: string): number | null {
  const formatter = formatterFor(tz);
  if (formatter === null) return null;
  const { year, month, day } = parseDate(personalDate);
  const wallAsUtc = Date.UTC(year, month - 1, day, 0, wallMin);
  const first = wallAsUtc - offsetAt(formatter, wallAsUtc);
  const second = wallAsUtc - offsetAt(formatter, first);
  return second;
}

/** ISO 8601 UTC string (second precision) for a wall time in `tz`, or null when `tz` is invalid. */
export function zonedWallToIso(personalDate: string, wallMin: number, tz: string): string | null {
  const epoch = zonedWallToEpoch(personalDate, wallMin, tz);
  if (epoch === null) return null;
  return new Date(Math.round(epoch / MS_PER_MINUTE) * MS_PER_MINUTE).toISOString();
}
